import {
  type ChangeEvent,
  cloneElement,
  type Dispatch,
  type DragEvent,
  type FC,
  type ReactElement,
  type SetStateAction,
  useMemo,
  useRef,
  useState,
} from 'react';

import imageCompression from 'browser-image-compression';
import { type AxiosProgressEvent } from 'axios/index';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { Label, Tooltip } from '@wf/sw-ui-kit';
import { FileUpload } from '@wf/components';

import { selectedLicenseTypeCodeSelector } from '../../modules/License/store/selectors';
import { type IAttachedDocumentResponse } from '../../modules/License/types/response';

import { uploadFile } from '../../services/attachmentService';

import { type FileType } from '../../constants';

import { getFileType, toasterError, toasterWarning } from '../../helpers';

import styles from './AIAttachmentUploader.module.scss';

interface IAIAttachmentUploaderProps {
  supportedFileTypes: FileType[];
  supportedMaxFileSizeInMB: number;
  maxCount: number;
  uploadCallback: (uploadData: IAttachedDocumentResponse[]) => void;
  onDeleteFile?: () => void;
  isUploading?: boolean;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
  setInvalidFiles: Dispatch<SetStateAction<string[]>>;
  errorMessage?: string;
  labelHelpText?: string;
  disabled?: boolean;
  className?: string;
  button?: ReactElement | null;
  showButtonTooltip?: boolean;
  isMultiple?: boolean;
}

const maxImageSizeInMB = 0.5;

const AIAttachmentUploader: FC<IAIAttachmentUploaderProps> = ({
  supportedFileTypes,
  supportedMaxFileSizeInMB,
  maxCount,
  uploadCallback,
  onDeleteFile,
  className,
  isUploading,
  setIsUploading,
  setInvalidFiles,
  button,
  showButtonTooltip,
  errorMessage,
  labelHelpText,
  isMultiple = true,
  disabled = false,
}) => {
  const licenseTypeCode = useSelector(selectedLicenseTypeCodeSelector);

  const [progressPercent, setProgressPercent] = useState(0);
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChooseFiles = () => {
    inputRef?.current?.click();
  };

  const supportedFileTypesRegexp = useMemo(
    () => new RegExp(supportedFileTypes.map((type) => `\\.${type}`).join('|'), 'i'),
    [supportedFileTypes]
  );

  const validateFileFormat = (file: File) => {
    const fileType = getFileType(file);

    return supportedFileTypesRegexp.test(file.name) || (fileType && supportedFileTypes.includes(fileType));
  };

  const validateFile = (file: File) => {
    const validSizeInKB = supportedMaxFileSizeInMB * 1024 * 1024;

    return file.size <= validSizeInKB && validateFileFormat(file);
  };

  const onUploadProgress = (e: AxiosProgressEvent) => {
    const progressPercent = Math.round((e.progress || 0) * 100);
    setProgressPercent(progressPercent);
  };

  const handleUpload = async (files: FileList) => {
    setIsUploading(true);
    setProgressPercent(0);
    const formData = new FormData();

    const uploadedFiles = isMultiple ? Array.from(files) : [files[0] as File];

    const { validFiles, invalidFiles } = uploadedFiles.reduce(
      (result, file) => {
        const isValid = validateFile(file);
        isValid ? result.validFiles.push(file) : result.invalidFiles.push(file);

        return result;
      },
      { validFiles: [] as File[], invalidFiles: [] as File[] }
    );

    if (!validFiles.length) {
      setIsUploading(false);
      toasterError({ description: 'Invalid file, check type and size' });

      return;
    }

    if (validFiles.length > maxCount) {
      toasterWarning({
        title: 'Maximum Number of Files Exceeded',
        description:
          'You can only upload up to {{maxCount}} files at a time. Please initiate another file upload for the rest of your files.',
        messageParams: { maxCount },
      });
    }

    const filesToUpload = await Promise.all(
      validFiles.slice(0, maxCount).map(
        (file) =>
          new Promise((resolve) => {
            const shouldCompress = file.size > maxImageSizeInMB * 1024 * 1024;

            if (!shouldCompress || !file.type.includes('image')) {
              resolve(file);

              return;
            }

            imageCompression(file, { maxSizeMB: maxImageSizeInMB })
              .then((res: File) => {
                const compressedFile = new File([res], res.name, {
                  lastModified: new Date().getTime(),
                  type: res.type,
                });
                resolve(compressedFile);
              })
              .catch(() => {
                resolve(file);
              });
          })
      )
    );

    filesToUpload.forEach((item) => {
      formData.append('files', item as Blob);
    });

    try {
      const { data } = await uploadFile(formData, licenseTypeCode as string, onUploadProgress);
      uploadCallback(data);

      if (invalidFiles.length) {
        setInvalidFiles(invalidFiles.map((f) => f.name));
      }
    } catch (error) {
      toasterError({ description: 'Unable to upload files' });
    } finally {
      inputRef.current && (inputRef.current.value = '');
      setIsUploading(false);
    }
  };

  const handleChangeFiles = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { files } = e.target;

    if (files) {
      void handleUpload(files);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.files?.length) {
      void handleUpload(e.dataTransfer.files);
    }
  };

  const handleDelete = () => {
    onDeleteFile?.();
  };

  const tooltipText = `${t('File supported')}: ${supportedFileTypes.join(', ')}
  ${t('Max Size')}: ${supportedMaxFileSizeInMB}MB`;

  return (
    <>
      <input
        onChange={handleChangeFiles}
        className={styles.fileInput}
        multiple={isMultiple}
        ref={inputRef}
        type="file"
      />
      {button ? (
        showButtonTooltip ? (
          <Tooltip tooltipContent={tooltipText} placement="top" offset={0}>
            {cloneElement(button, { onClick: handleChooseFiles })}
          </Tooltip>
        ) : (
          <>
            <span className={cn(styles.helpText)}>{tooltipText}</span>
            {cloneElement(button, { onClick: handleChooseFiles })}
          </>
        )
      ) : (
        <div className={styles.uploaderWrapper}>
          <Label helpText={labelHelpText}>{t('Upload File')}</Label>
          <FileUpload
            uploadProgressPercentage={isUploading ? progressPercent : undefined}
            supportedMaxFileSize={`${supportedMaxFileSizeInMB}MB`}
            onDelete={!isUploading ? handleDelete : undefined}
            fileName={isUploading ? t('Uploading...') : ''}
            supportedFileTypes={supportedFileTypes}
            onChooseFile={handleChooseFiles}
            className={className}
            onDrop={handleDrop}
            disabled={disabled}
            t={t}
          />
          {errorMessage && <div className={styles.errorMessage}>{t(errorMessage)}</div>}
        </div>
      )}
    </>
  );
};

export default AIAttachmentUploader;
